import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'
import type { CampaignInstrument } from '@/features/campaigns/types'
import { instrumentApiPath } from '@/features/instruments/infra/instrumentPaths'
import type { QuestionRoutingRules } from '@/features/instruments/domain/routing'
import type { ChoiceTextResponseDraft } from '@/features/instruments/domain/choiceTextResponse'
import type { IInstrumentLiveEditState, ProfileSystemKey } from '@brushfire/types'

/** Large drafts (picture-choice data URLs) need more than the default 1s user-path budget. */
export const INSTRUMENT_DRAFT_PATCH_TIMEOUT_MS = 30_000

export type InstrumentDraftQuestionType =
  | 'multiple_choice'
  | 'yes_no'
  | 'checkbox'
  | 'dropdown'
  | 'picture_choice'
  | 'ranking'
  | 'short_text'
  | 'long_text'
  | 'date'
  | 'rating'
  | 'likert'
  | 'slider'
  | 'video'

export type InstrumentDraftOption = { id: string; label: string; imageUrl?: string }

/** Mirrors campaign-service `PATCH …/draft` JSON body (US-IB-03). */
export type InstrumentDraftQuestion = {
  id: string
  order: number
  type: InstrumentDraftQuestionType
  prompt: string
  description?: string | null
  mandatory?: boolean
  /** BFP-1070 — exclude from dashboards/reports; still shown to responders. */
  hidden?: boolean
  options?: InstrumentDraftOption[]
  minSelections?: number
  maxSelections?: number
  yesLabel?: string
  noLabel?: string
  showSelectAll?: boolean
  selectAllLabel?: string
  randomizeOrder?: boolean
  layoutHorizontal?: boolean
  multiColumn?: boolean
  yesNoToggleStyle?: boolean
  hasOtherOption?: boolean
  hasNoneOfAboveOption?: boolean
  /** BFP-1156 — MC / checkbox option that collects free text; `null` = off. */
  textResponse?: ChoiceTextResponseDraft | null
  imageSize?: 'small' | 'medium' | 'large'
  /** When `type` is `short_text` or `long_text`, optional min/max characters for the answer. */
  minLength?: number
  /** When `type` is `short_text` or `long_text`, optional max characters for the answer. */
  maxLength?: number
  /** When `type` is `short_text`, `long_text`, or `date`, optional placeholder in the respondent input. */
  placeholder?: string
  /** When `type` is `date`, optional bounds and restriction toggles (ISO `yyyy-mm-dd`). */
  earliestSelectableDate?: string
  latestSelectableDate?: string
  disablePastDates?: boolean
  disableFutureDates?: boolean
  /** When `type` is `date`, optional respondent-facing layout (matches builder display format). */
  dateInputFormat?: 'dd/MM/yyyy' | 'MM/dd/yyyy'
  /** When `type` is `rating`, max scale value (5 or 10). */
  ratingScaleMax?: 5 | 10
  /** When `type` is `rating`, visual style. */
  ratingDisplayStyle?: 'stars' | 'numbers'
  /** When `type` is `rating`, optional low-end label. */
  ratingLowLabel?: string
  /** When `type` is `rating`, optional high-end label. */
  ratingHighLabel?: string
  /** When `type` is `likert`, number of agreement scale points (1–10). */
  likertPointCount?: number
  /** When `type` is `likert`, respondent label per point (1..N). */
  likertLabels?: string[]
  /** When `type` is `likert`, lay out scale options in a row vs column. */
  likertLayoutHorizontal?: boolean
  /** When `type` is `slider`, inclusive integer minimum. */
  sliderMinValue?: number
  /** When `type` is `slider`, inclusive integer maximum. */
  sliderMaxValue?: number
  /** When `type` is `slider`, positive integer step. */
  sliderStep?: number
  /** When `type` is `slider`, optional label at the minimum end of the track. */
  sliderLowLabel?: string
  /** When `type` is `slider`, optional label at the maximum end of the track. */
  sliderHighLabel?: string
  /** When `type` is `video` — YouTube / Vimeo / direct MP4 URL or uploaded serve path. */
  videoUrl?: string
  /** When `type` is `video` — how the source was added. */
  videoSource?: 'link' | 'upload'
  /** Authoring-only — cloned from instrument template; locks type in builder. */
  fromTemplate?: boolean
  /** BFP-601 — bank entry id when imported from Question Bank. */
  questionBankEntryId?: string
  /** BFP-1143 — profile-update questions sync the answer onto the responder profile. */
  isProfileUpdate?: boolean
  profileAttributeId?: string
  freshnessTag?: 'PERM' | 'R1M' | 'R3M' | 'R6M' | 'R12M'
  systemKey?: ProfileSystemKey | null
}

export type PatchInstrumentDraftBody = {
  questions?: InstrumentDraftQuestion[]
  routingRules?: QuestionRoutingRules[]
  /** Metadata-only patch (continue-draft info step); does not replace builder questions. */
  name?: string
  description?: string
}

/** `data` from `success()` envelope — GET/PATCH draft. ECT is seconds only; derive display in the UI. */
export type InstrumentDraftPatchResult = {
  estimatedCompletionSeconds: number
  updatedAt: string
  questions: InstrumentDraftQuestion[]
  routingRules?: QuestionRoutingRules[]
  /** BFP-1142 — present while editing a Live / Paused survey. */
  liveEdit?: IInstrumentLiveEditState
}

export function instrumentDraftPath(instrumentId: string): string {
  return instrumentApiPath(instrumentId, '/draft')
}

export function instrumentOptionImagePath(instrumentId: string): string {
  return instrumentApiPath(instrumentId, '/draft/option-image')
}

export function instrumentQuestionVideoPath(instrumentId: string): string {
  return instrumentApiPath(instrumentId, '/draft/question-video')
}

export type UploadInstrumentQuestionVideoResult = {
  questionId: string
  videoUrl: string
}

/** Upload a question video (MP4, max 200 MB) to MinIO; persists URL only in draft JSON. */
export async function uploadInstrumentQuestionVideo(params: {
  campaignId?: string
  instrumentId: string
  questionId: string
  file: File
  signal?: AbortSignal
}): Promise<UploadInstrumentQuestionVideoResult> {
  const form = new FormData()
  form.append('questionId', params.questionId)
  form.append('video', params.file, params.file.name || 'video.mp4')
  const { data: envelope } = await apiClient.post<
    ApiResponse<UploadInstrumentQuestionVideoResult>
  >(instrumentQuestionVideoPath(params.instrumentId), form, {
    signal: params.signal,
    timeout: 180_000,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  if (!envelope?.data?.videoUrl) {
    throw new Error('Question video upload response missing videoUrl')
  }
  return envelope.data
}

export type UploadInstrumentOptionImageResult = {
  optionId: string
  imageUrl: string
}

/** Upload picture-choice / ranking option image to MinIO; persists URL only in draft JSON. */
export async function uploadInstrumentOptionImage(params: {
  /** Retained for call-site context; not sent in the URL. */
  campaignId?: string
  instrumentId: string
  optionId: string
  file: File
  signal?: AbortSignal
}): Promise<UploadInstrumentOptionImageResult> {
  const form = new FormData()
  form.append('optionId', params.optionId)
  form.append('image', params.file, params.file.name || 'image')
  const { data: envelope } = await apiClient.post<
    ApiResponse<UploadInstrumentOptionImageResult>
  >(instrumentOptionImagePath(params.instrumentId), form, {
    signal: params.signal,
    timeout: INSTRUMENT_DRAFT_PATCH_TIMEOUT_MS,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  if (!envelope?.data?.imageUrl) {
    throw new Error('Option image upload response missing imageUrl')
  }
  return envelope.data
}

/** `PATCH …/instruments/:instrumentId` — name/description only (US-IB-03 info continue). */
export function instrumentMetadataPath(instrumentId: string): string {
  return instrumentApiPath(instrumentId)
}

export type PatchInstrumentMetadataBody = {
  name: string
  description?: string
}

export async function patchInstrumentMetadata(params: {
  campaignId?: string
  instrumentId: string
  body: PatchInstrumentMetadataBody
  signal?: AbortSignal
}): Promise<CampaignInstrument> {
  const { data: envelope } = await apiClient.patch<ApiResponse<CampaignInstrument>>(
    instrumentMetadataPath(params.instrumentId),
    params.body,
    { signal: params.signal },
  )
  if (!envelope?.data) {
    throw new Error('Instrument update response missing data')
  }
  return envelope.data
}

/**
 * Persists instrument builder draft via campaign-service (US-IB-03).
 * Sends required `Idempotency-Key` (UUIDv4); generates one when omitted.
 */
export async function getInstrumentDraft(params: {
  campaignId?: string
  instrumentId: string
  signal?: AbortSignal
}): Promise<InstrumentDraftPatchResult> {
  const { data: envelope } = await apiClient.get<ApiResponse<InstrumentDraftPatchResult>>(
    instrumentDraftPath(params.instrumentId),
    { signal: params.signal },
  )
  if (!envelope?.data || typeof envelope.data.estimatedCompletionSeconds !== 'number') {
    throw new Error('Instrument draft response missing data')
  }
  return envelope.data
}

export async function patchInstrumentDraft(params: {
  campaignId?: string
  instrumentId: string
  body: PatchInstrumentDraftBody
  idempotencyKey?: string
  signal?: AbortSignal
}): Promise<InstrumentDraftPatchResult> {
  const idempotencyKey =
    params.idempotencyKey ?? globalThis.crypto.randomUUID()
  const { data: envelope } = await apiClient.patch<
    ApiResponse<InstrumentDraftPatchResult>
  >(
    instrumentDraftPath(params.instrumentId),
    params.body,
    {
      timeout: INSTRUMENT_DRAFT_PATCH_TIMEOUT_MS,
      signal: params.signal,
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
    },
  )
  if (!envelope?.data || typeof envelope.data.estimatedCompletionSeconds !== 'number') {
    throw new Error('Instrument draft save response missing data')
  }
  return envelope.data
}
